/**
 * KnowledgeBase — «База знань».
 *
 * Дві половини:
 *  1. Конспекти — власні нотатки (Markdown + KaTeX), з тегами і пошуком.
 *  2. Генерація — завантаження PDF/скриншотів або вставка тексту, Gemini
 *     повертає картки, PreviewModal показує їх, і лише після «Зберегти» вони
 *     їдуть у D1.
 */

import { useCallback, useEffect, useState } from 'react'
import {
  FileText,
  Loader2,
  NotebookPen,
  Pin,
  PinOff,
  Plus,
  Save,
  Search,
  Sparkles,
  Trash2,
  X,
} from 'lucide-react'

import { Modal, Field, Alert, Empty, Loading, Segmented } from './ui.jsx'
import RichText from './RichText.jsx'
import GeneratePanel from './GeneratePanel.jsx'
import PreviewModal from './PreviewModal.jsx'
import { api } from '../lib/api.js'
import { SUBJECTS } from '../lib/constants.js'
import { parseTagInput, normalizeTag, splitTagList } from '../lib/tags.js'

const VIEWS = [
  { value: 'notes', label: 'Конспекти' },
  { value: 'generate', label: 'Генерація карток' },
]

const emptyNote = () => ({ id: null, title: '', subject: 'математика', body: '', tags: [], deck_id: null, pinned: false })

export default function KnowledgeBase({ settings, decks = [], onNeedSettings, onCardsAdded, dueNow = 0, onStartReview }) {
  const [view, setView] = useState('notes')
  const [notes, setNotes] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [search, setSearch] = useState('')
  const [editing, setEditing] = useState(null)

  // Попередній перегляд згенерованих карток разом із визначеним контекстом
  const [preview, setPreview] = useState(null)
  const [savingCards, setSavingCards] = useState(false)
  const [saveError, setSaveError] = useState(null)
  const [saveNote, setSaveNote] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setNotes(await api.listNotes({ q: search.trim() || undefined }))
    } catch (err) {
      setError(err?.message ?? 'Не вдалося завантажити конспекти.')
    } finally {
      setLoading(false)
    }
  }, [search])

  useEffect(() => {
    const t = setTimeout(load, search ? 300 : 0)
    return () => clearTimeout(t)
  }, [load, search])

  const removeNote = async (note) => {
    if (!confirm(`Видалити конспект «${note.title}»?`)) return
    try {
      await api.deleteNote(note.id)
      await load()
    } catch (err) {
      setError(err?.message ?? 'Не вдалося видалити конспект.')
    }
  }

  /**
   * Картки з PreviewModal → D1. Разом із самими картками йде контекст,
   * визначений Gemini: предмет, тема й, за бажанням, конкретна колода.
   * Якщо колоду не задано, сервер знайде її за «предмет + тема» або створить.
   */
  const saveCards = useCallback(
    async (cards, { deckId, subject, topic, sourceLabel, dropTags } = {}) => {
      setSavingCards(true)
      setSaveError(null)
      try {
        const res = await api.importCards({ cards, deckId: deckId ?? null, subject, topic, sourceLabel, dropTags })
        setPreview(null)
        onCardsAdded?.()
        setView('notes')
        setSaveNote(
          res?.deck
            ? res.deck.created
              ? `Картки збережено. Створено нову колоду «${res.deck.title}».`
              : `Картки збережено в колоду «${res.deck.title}».`
            : null,
        )
      } catch (err) {
        setSaveError(err?.message ?? 'Не вдалося зберегти картки.')
      } finally {
        setSavingCards(false)
      }
    },
    [onCardsAdded],
  )

  return (
    <div className="space-y-7">
      {view === 'notes' && (
        <section className="knowledge-brief">
          <div><p className="section-kicker">На сьогодні</p><h2>{dueNow ? `${dueNow} карток чекають на вас` : 'Повторення на сьогодні завершено'}</h2><p>{dueNow ? 'Коротка сесія допоможе закріпити матеріал, поки він свіжий.' : 'Можна поповнити базу новим матеріалом або переглянути конспекти.'}</p></div>
          <div className="knowledge-brief-actions"><button type="button" onClick={onStartReview} className="btn-primary">{dueNow ? 'Почати повторення' : 'Відкрити картки'}</button><button type="button" onClick={() => setView('generate')} className="btn-outline"><Sparkles className="h-4 w-4" /> Згенерувати</button></div>
        </section>
      )}

      <div className="knowledge-toolbar">
        <div className="toolbar-views"><p className="section-kicker">Матеріали</p><Segmented value={view} onChange={setView} options={VIEWS} /></div>

        {view === 'notes' && (
          <div className={`knowledge-search search-field ${search ? 'has-query' : ''}`}>
            <p className="section-kicker">Пошук</p>
            <div className="relative">
              <Search className="pointer-events-none absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <input
                className="input !pl-10"
                placeholder="Шукайте за назвою, тегом або фрагментом конспекту…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Пошук у конспектах"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  className="absolute top-1/2 right-2 -translate-y-1/2 rounded p-1 text-slate-400 hover:text-slate-200"
                  aria-label="Очистити пошук"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>
        )}

        <button
          type="button"
          onClick={() => setEditing(emptyNote())}
          className="btn-primary toolbar-create shrink-0 whitespace-nowrap"
          aria-label="Новий конспект"
        >
          <Plus className="h-4 w-4" /> <span>Конспект</span>
        </button>
      </div>

      {error && <Alert tone="error" title="Помилка">{error}</Alert>}

      {saveNote && <Alert tone="ok" className="mb-3">{saveNote}</Alert>}

      {/* ── Конспекти ── */}
      {view === 'notes' &&
        (loading ? (
          <Loading label="Завантажую конспекти…" />
        ) : !notes.length ? (
          <Empty
            icon={NotebookPen}
            title={search ? 'Нічого не знайдено' : 'Конспектів ще немає'}
            action={
              <button type="button" onClick={() => setView('generate')} className="btn-primary">
                <Sparkles className="h-4 w-4" /> Згенерувати з матеріалу
              </button>
            }
          >
            {search
              ? 'Спробуйте інший запит.'
              : 'Додайте власний конспект або згенеруйте картки зі світлини чи PDF — це найшвидший шлях.'}
          </Empty>
        ) : (
          <div className="workspace-grid">
          <ul className="note-list">
            {notes.map((note) => {
              const subjectMeta = SUBJECTS.find((s) => s.id === note.subject)
              return (
                <li key={note.id} className={`note-row ${note.pinned ? 'is-pinned' : ''}`}>
                  <div className="note-index">{String(note.id).padStart(2, '0')}</div>
                  <div className="flex min-w-0 flex-1 items-start gap-3">
                    {subjectMeta && (
                      <span className={`note-subject ${subjectMeta.bg}`} title={subjectMeta.label}>
                        <subjectMeta.icon className="h-4 w-4" />
                      </span>
                    )}
                    <div className="min-w-0 flex-1"><button
                      type="button"
                      onClick={() => setEditing({ ...note, tags: splitTagList(note.tag_list) })}
                      className="min-w-0 flex-1 text-left"
                    >
                      <h3 className="truncate font-semibold hover:underline">{note.title || 'Без назви'}</h3>
                    </button>
                    <div className="mt-1 line-clamp-2 text-sm text-slate-600 dark:text-slate-400">
                    <RichText tags={splitTagList(note.tag_list)}>{note.body}</RichText>
                    </div></div>
                  </div>
                  <div className="note-meta">
                    {note.pinned && <Pin className="h-3.5 w-3.5 text-slate-200" />}
                    {splitTagList(note.tag_list).slice(0, 4).map((t) => (
                      <span key={t} className="chip">
                        #{t}
                      </span>
                    ))}
                    <div className="ml-auto flex items-center gap-0.5">
                      <button
                        type="button"
                        onClick={() =>
                          setEditing({ ...note, tags: splitTagList(note.tag_list), pinned: !note.pinned })
                        }
                        className="btn-ghost !px-1.5 !py-1"
                        title={note.pinned ? 'Відкріпити' : 'Закріпити'}
                      >
                        {note.pinned ? <PinOff className="h-3.5 w-3.5" /> : <Pin className="h-3.5 w-3.5" />}
                      </button>
                      <button
                        type="button"
                        onClick={() => removeNote(note)}
                        className="btn-ghost !px-1.5 !py-1 text-rose-600 dark:text-rose-400"
                        title="Видалити"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  </div>
                </li>
              )
            })}
          </ul>
          <aside className="workspace-rail"><p className="section-kicker">У базі</p><b className="rail-number">{notes.length}</b><p className="mt-1 text-sm text-slate-500">конспектів доступно для роботи</p><div className="rail-rule" /><p className="section-kicker">Швидкий старт</p><button type="button" onClick={() => setEditing(emptyNote())}>Новий конспект</button><button type="button" onClick={() => setView('generate')}>З матеріалу</button></aside>
          </div>
        ))}

      {/* ── Генерація ── */}
      {view === 'generate' && (
        <div className="generation-workflow">
          <header><p className="section-kicker">01 — джерело · 02 — вимоги · 03 — review</p><h2>Перетворіть матеріал на картки</h2><p>Додайте текст або файл. Предмет і тему визначить модель, а перед збереженням усе можна перевірити.</p></header>
          <GeneratePanel
            settings={settings}
            deckId={decks.length === 1 ? decks[0].id : null}
            onNeedKeys={onNeedSettings}
            onGenerated={(result) => {
              setSaveError(null)
              setSaveNote(null)
              setPreview(result)
            }}
          />
        </div>
      )}

      {/* ── Редактор конспекту ── */}
      {editing && (
        <NoteEditor
          note={editing}
          decks={decks}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null)
            await load()
          }}
        />
      )}

      {/* ── Попередній перегляд карток ── */}
      <PreviewModal
        open={!!preview}
        cards={preview?.cards ?? []}
        decks={decks}
        subject={preview?.subject}
        subjectLabel={preview?.subjectLabel}
        subjectSource={preview?.subjectSource}
        topic={preview?.topic}
        topicTag={preview?.topicTag}
        totalCardsGenerated={preview?.totalCardsGenerated}
        warnings={preview?.warnings}
        sourceLabel={preview?.sourceLabel}
        settings={settings}
        saving={savingCards}
        error={saveError}
        onClose={() => {
          setPreview(null)
          setSaveError(null)
        }}
        onSave={saveCards}
      />
    </div>
  )
}

// ─────────────────────────────────────────────────────────────────────────────

function NoteEditor({ note: initial, decks, onClose, onSaved }) {
  const [note, setNote] = useState(initial)
  const [tagInput, setTagInput] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [tab, setTab] = useState('edit')

  const set = (patch) => setNote((n) => ({ ...n, ...patch }))

  const commitTag = () => {
    const t = normalizeTag(tagInput)
    if (t && !note.tags.includes(t)) set({ tags: [...note.tags, t] })
    setTagInput('')
  }

  const submit = async () => {
    if (!note.title.trim()) {
      setError('Дайте конспекту назву.')
      return
    }
    setSaving(true)
    setError(null)
    try {
      const payload = {
        title: note.title.trim(),
        subject: note.subject,
        body: note.body,
        tags: parseTagInput(note.tags),
        deck_id: note.deck_id || null,
        pinned: note.pinned ? 1 : 0,
      }
      if (note.id) await api.updateNote({ id: note.id, ...payload })
      else await api.createNote(payload)
      await onSaved()
    } catch (err) {
      setError(err?.message ?? 'Не вдалося зберегти конспект.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      size="xl"
      title={initial.id ? 'Редагувати конспект' : 'Новий конспект'}
      footer={
        <>
          <button type="button" onClick={onClose} className="btn-ghost" disabled={saving}>
            Скасувати
          </button>
          <button type="button" onClick={submit} className="btn-primary" disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Зберегти
          </button>
        </>
      }
    >
      {error && <Alert tone="error" className="mb-3">{error}</Alert>}

      <div className="space-y-3">
        <div className="grid gap-3 sm:grid-cols-[1fr_14rem]">
          <Field label="Назва" required>
            <input
              className="input"
              value={note.title}
              onChange={(e) => set({ title: e.target.value })}
              placeholder="Напр.: Тригонометрія — формули"
            />
          </Field>
          <Field label="Предмет">
            <select className="input" value={note.subject} onChange={(e) => set({ subject: e.target.value })}>
              {SUBJECTS.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.label}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Теги" hint="Enter — додати">
            <div className="flex flex-wrap items-center gap-1.5">
              {note.tags.map((t) => (
                <span key={t} className="chip">
                  #{t}
                  <button
                    type="button"
                    onClick={() => set({ tags: note.tags.filter((x) => x !== t) })}
                    className="ml-0.5 opacity-60 hover:opacity-100"
                    aria-label={`Прибрати ${t}`}
                  >
                    <X className="w-3 h-3" />
                  </button>
                </span>
              ))}
              <input
                className="input !py-1 text-xs"
                value={tagInput}
                onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ',') {
                    e.preventDefault()
                    commitTag()
                  }
                }}
                placeholder="додати тег…"
              />
            </div>
          </Field>

          <Field label="Колода">
            <select
              className="input"
              value={note.deck_id ?? ''}
              onChange={(e) => set({ deck_id: e.target.value ? Number(e.target.value) : null })}
            >
              <option value="">— без колоди —</option>
              {decks.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.title}
                </option>
              ))}
            </select>
          </Field>
        </div>

        <div>
          <div className="mb-2 flex items-center gap-2">
            <Segmented
              value={tab}
              onChange={setTab}
              options={[
                { value: 'edit', label: 'Текст', icon: <NotebookPen className="h-4 w-4" /> },
                { value: 'preview', label: 'Перегляд', icon: <FileText className="h-4 w-4" /> },
              ]}
            />
            <label className="ml-auto flex items-center gap-1.5 text-sm text-slate-500 dark:text-slate-400">
              <input
                type="checkbox"
                checked={!!note.pinned}
                onChange={(e) => set({ pinned: e.target.checked })}
                className="h-4 w-4 accent-brand-600"
              />
              Закріпити вгорі
            </label>
          </div>

          {tab === 'edit' ? (
            <textarea
              className="input min-h-72 resize-y font-mono text-[13px]"
              value={note.body}
              onChange={(e) => set({ body: e.target.value })}
              placeholder={'Markdown + KaTeX:\n\n## Тригонометрія\n\nФормули: $a^2 + b^2 = c^2$\n\n- таблиця\n- дата'}
            />
          ) : (
            <div className="card-surface min-h-72 p-4">
              {note.body.trim() ? (
                <RichText tags={note.tags}>{note.body}</RichText>
              ) : (
                <p className="text-sm text-slate-400 italic">Порожньо — немає чого показувати.</p>
              )}
            </div>
          )}
        </div>

        <p className="text-xs text-slate-500 dark:text-slate-400">
          Підтримується Markdown і LaTeX: <code className="font-mono">$x^2$</code> — інлайн,{' '}
          <code className="font-mono">$$…$$</code> — окремим блоком. Щоб підсвітити наголос у слові,
          поставте комбінований знак <code className="font-mono">U+0301</code> просто після ударної
          букви й додайте тег <code className="font-mono">наголоси</code>.
        </p>
      </div>
    </Modal>
  )
}
